import { c as _c } from "react-compiler-runtime";
import { useEffect, useMemo, useState } from 'react';
import debounce from 'transmute/debounce';
import { ASSET_ADDED_TO_FOLDER } from 'framework-listing-lib/constants/folderEvents';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { FOLDERS_POLLING_INTERVAL } from 'framework-listing-lib/internal/Folders/constants/folders';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import useFolderMoveHistoryState from './useFolderMoveHistoryState';

/**
 * This hook toggles on/off the polling state for folders.
 * Given the known latency that exists in the CRM, we poll for new information
 * 3 times every @FOLDERS_POLLING_INTERVAL seconds.
 */
export default function useFolderPollingState() {
  "use memo";

  const $ = _c(12);
  const [pollingState, setPollingState] = useState("OFF");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [affectedFolderIds, setAffectedFolderIds] = useState(t0);
  const isFoldersMode = useIsFoldersMode();
  const [, setHistoryState] = useFolderMoveHistoryState();
  let t1;
  if ($[1] !== setHistoryState) {
    t1 = debounce(3 * FOLDERS_POLLING_INTERVAL, () => {
      setPollingState("OFF");
      setAffectedFolderIds([]);
      setHistoryState({});
    });
    $[1] = setHistoryState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const debouncedStopPolling = t1;
  let t2;
  if ($[3] !== debouncedStopPolling) {
    t2 = debounce(2000, () => {
      setPollingState("ON");
      debouncedStopPolling();
    });
    $[3] = debouncedStopPolling;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const debouncedStartPolling = t2;
  let t3;
  let t4;
  if ($[5] !== debouncedStartPolling || $[6] !== isFoldersMode) {
    t3 = () => {
      const enableFolderPolling = response => {
        if (!isFoldersMode) {
          return;
        }
        const {
          newFolderId
        } = response;
        setAffectedFolderIds(prev => [...new Set([...prev, newFolderId])]);
        debouncedStartPolling();
      };
      listingLibObserver.on(ASSET_ADDED_TO_FOLDER, enableFolderPolling);
      return () => {
        listingLibObserver.off(ASSET_ADDED_TO_FOLDER, enableFolderPolling);
      };
    };
    t4 = [debouncedStartPolling, isFoldersMode];
    $[5] = debouncedStartPolling;
    $[6] = isFoldersMode;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== affectedFolderIds || $[10] !== pollingState) {
    t5 = {
      affectedFolderIds,
      pollingState
    };
    $[9] = affectedFolderIds;
    $[10] = pollingState;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}